// El executor de las herramientas del calendario — ADR-2026-10-06-el-agente-planifica-el-calendario.
//
// Usa los MISMOS servicios que los botones del editor (`crearPost`, `guardarPost`,
// `sumarGeneradasAProducto`, `quitarImagen`, `borrarPost`): el agente no tiene un camino
// propio a la base que se salte las reglas de la pantalla. Lee siempre de la base y no del
// estado de React, que después de escribir tarda un render en ponerse al día.

import type { ResultadoDeHerramientaDePantalla } from '../../../../chat-atlas';
import { fetchGeneradasParaElegir, quitarImagen, sumarGeneradasAProducto } from '../../lanzamientosApi';
import { sugerirMomento, type PostEnAgenda } from '../mejorMomento';
import { MAX_PIEZAS, MAX_TEXTO, formatoDe, nombreDelFormato, problemasDelPost } from '../post';
import {
  borrarPost,
  crearPost,
  creacionesPorId,
  fetchAgendaDeLaEmpresa,
  fetchPosts,
  guardarPost,
  type PostConMiniaturas,
} from '../postsApi';
import { MAX_CREACIONES_POR_LLAMADA, MAX_POSTS_POR_LLAMADA } from './herramientas';

/** Lo que el executor necesita de la pantalla montada. */
export interface PantallaDelCalendario {
  lanzamientoId: string;
  companyId: string;
  /** Relee y redibuja el calendario después de escribir. */
  recargar: () => Promise<void>;
  /**
   * Abre el post en el editor y lleva la grilla a su mes. `'esperando'` si había otro post
   * abierto con cambios: se le preguntó al usuario si los guarda y se abre cuando elija.
   */
  abrirPost: (id: string, cuando: Date | null) => 'abierto' | 'esperando';
  /** Lleva la grilla al mes de esa fecha, para que se vea lo que se acaba de armar. */
  mostrarMes: (d: Date) => void;
  /** Marca los posts que armó el agente, para que se distingan en la grilla. */
  marcarDelAgente: (ids: string[]) => void;
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIA_MS = 24 * 60 * 60 * 1000;
const p2 = (n: number) => String(n).padStart(2, '0');

/** "2026-10-14" → medianoche local de ese día, o null. */
export function leerFecha(s: unknown): Date | null {
  const m = typeof s === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim()) : null;
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}

/**
 * "2026-10-14 19:00" (o con "T") → esa hora LOCAL, o null.
 *
 * Por componentes, como el editor: `new Date("2026-10-14T19:00")` la interpreta distinto
 * cada navegador, y un ISO con zona del modelo movería el post de hora.
 */
export function leerFechaYHora(s: unknown): Date | null {
  const m = typeof s === 'string' ? /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})$/.exec(s.trim()) : null;
  if (!m) return null;
  const [a, mes, dia, hh, mm] = m.slice(1).map(Number) as [number, number, number, number, number];
  if (hh > 23 || mm > 59) return null;
  const d = new Date(a, mes - 1, dia, hh, mm);
  return d.getMonth() === mes - 1 ? d : null;
}

/** Cómo se le cuenta una fecha al modelo: el formato que acepta de vuelta + el día. */
export function fechaParaElModelo(d: Date): string {
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(
    d.getMinutes(),
  )} (${DIAS[d.getDay()]})`;
}

const ok = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto });
const fallo = (texto: string): ResultadoDeHerramientaDePantalla => ({ content: texto, is_error: true });

export const listaDeIds = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim())
    : [];

/** Un texto en la lista: entero si es corto o si se pidió el post completo. */
function textoDe(t: string, max: number, completo: boolean): string {
  const limpio = t.trim();
  if (completo || limpio.length <= max) return `"${limpio}"`;
  return `"${limpio.slice(0, max)}…" (cortado: pedí ver_calendario con su post_id para leerlo entero)`;
}

function lineaDelPost(p: PostConMiniaturas, completo = false): string {
  const cuando = p.cuando ? fechaParaElModelo(p.cuando) : 'sin fecha';
  const piezas = p.medios
    .map(
      (m) =>
        `pieza_id=${m.id}${m.generadaId ? ` image_id=${m.generadaId}` : ' (subida a mano)'}${m.esVideo ? ' video' : ''}`,
    )
    .join(', ');
  const falta = problemasDelPost(p);
  return [
    `• post_id=${p.id} · ${cuando} · ${nombreDelFormato(formatoDe(p.medios))}`,
    p.estado !== 'pendiente' ? ` · ${p.estado.toUpperCase()}` : '',
    `\n  piezas: ${piezas || 'ninguna'}`,
    `\n  texto: ${p.texto.trim() ? textoDe(p.texto, 160, completo) : 'sin texto'}`,
    p.planificacion.trim() ? `\n  planificación: ${textoDe(p.planificacion, 200, completo)}` : '',
    p.referencias.trim() ? `\n  referencias: ${textoDe(p.referencias, 200, completo)}` : '',
    falta.length ? `\n  falta: ${falta.join('; ')}` : '\n  listo',
  ].join('');
}

/**
 * Suma creaciones a un post, validando que sean de la empresa y que entren. Exportada para
 * `mandar_a_instagram` del chat (ADR-2026-10-13-mandar-a-instagram-desde-el-chat): el post
 * que arma el chat cuelga sus piezas por el MISMO camino que el que arma el calendario.
 */
export async function colgarPiezas(
  companyId: string,
  postId: string,
  imageIds: string[],
  yaTiene: number,
): Promise<{ colgadas: number; problema: string | null; cuales: string }> {
  if (imageIds.length === 0) return { colgadas: 0, problema: null, cuales: '' };
  if (yaTiene + imageIds.length > MAX_PIEZAS) {
    return { colgadas: 0, problema: `un post admite hasta ${MAX_PIEZAS} piezas`, cuales: '' };
  }
  const conocidas = await creacionesPorId(companyId, imageIds);
  const desconocidas = imageIds.filter((id) => !conocidas.has(id));
  const validas = imageIds.filter((id) => conocidas.has(id));
  const r = await sumarGeneradasAProducto(
    postId,
    companyId,
    validas.map((id) => ({ id, varianteId: conocidas.get(id)?.varianteId ?? null })),
  );
  const problemas = [
    desconocidas.length ? `estos image_id no son creaciones de la marca: ${desconocidas.join(', ')}` : '',
    r.error ?? '',
  ].filter(Boolean);
  // Qué se colgó, con lo que muestra cada una: si no es lo que el agente quería, lo ve acá.
  const cuales = validas
    .map(
      (id) => `image_id=${id}${conocidas.get(id)?.queMuestra ? ` («${conocidas.get(id)!.queMuestra}»)` : ''}`,
    )
    .join(', ');
  return { colgadas: r.agregadas, problema: problemas.length ? problemas.join('; ') : null, cuales };
}

export async function ejecutarHerramientaDelCalendario(
  nombre: string,
  input: Record<string, unknown>,
  pantalla: PantallaDelCalendario,
): Promise<ResultadoDeHerramientaDePantalla> {
  const ahora = new Date();
  switch (nombre) {
    case 'ver_calendario': {
      // Un post entero: lo que se lee antes de modificar su texto (review Greptile #169).
      if (typeof input.post_id === 'string' && input.post_id.trim()) {
        const { posts, error } = await fetchPosts(pantalla.lanzamientoId, pantalla.companyId);
        if (error) return fallo(error);
        const post = posts.find((p) => p.id === input.post_id);
        if (!post) return fallo(`No hay un post con post_id=${input.post_id} en este calendario.`);
        return ok(lineaDelPost(post, true));
      }
      const desde =
        leerFecha(input.desde) ?? new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const hasta = leerFecha(input.hasta) ?? new Date(desde.getTime() + 45 * DIA_MS);
      const finDeHasta = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate(), 23, 59, 59);
      const { posts, error } = await fetchPosts(pantalla.lanzamientoId, pantalla.companyId);
      if (error) return fallo(error);
      const enRango = posts.filter((p) => !p.cuando || (p.cuando >= desde && p.cuando <= finDeHasta));
      const antes = posts.filter((p) => p.cuando && p.cuando < desde).length;
      const despues = posts.filter((p) => p.cuando && p.cuando > finDeHasta).length;
      const cabeza =
        `Hoy es ${fechaParaElModelo(ahora)}. Del ${fechaParaElModelo(desde).slice(0, 10)} al ` +
        `${fechaParaElModelo(hasta).slice(0, 10)} hay ${enRango.length} post${enRango.length === 1 ? '' : 's'}` +
        (antes || despues ? ` (fuera de ese rango: ${antes} antes y ${despues} después)` : '') +
        '.';
      return ok([cabeza, ...enRango.map((p) => lineaDelPost(p))].join('\n'));
    }

    case 'ver_creaciones_para_posts': {
      const cantidad = Math.min(
        MAX_CREACIONES_POR_LLAMADA,
        Math.max(1, Number(input.cantidad) || MAX_CREACIONES_POR_LLAMADA),
      );
      const saltear = Math.max(0, Number(input.saltear) || 0);
      const [todas, { posts }] = await Promise.all([
        fetchGeneradasParaElegir(pantalla.companyId, [], [], true),
        fetchPosts(pantalla.lanzamientoId, pantalla.companyId),
      ]);
      const usadas = new Set(posts.flatMap((p) => p.medios.map((m) => m.generadaId)).filter(Boolean));
      const filtradas = input.solo_videos === true ? todas.filter((c) => c.esVideo) : todas;
      const pagina = filtradas.slice(saltear, saltear + cantidad);
      const descripciones = await creacionesPorId(
        pantalla.companyId,
        pagina.map((c) => c.id),
      );
      if (pagina.length === 0) {
        return ok(
          saltear > 0
            ? 'No hay más creaciones.'
            : 'La marca todavía no tiene creaciones que sirvan para un post. Se generan desde el chat o los modos.',
        );
      }
      const bloques: unknown[] = [
        {
          type: 'text',
          text:
            `Creaciones ${saltear + 1} a ${saltear + pagina.length} de ${filtradas.length}` +
            (saltear + pagina.length < filtradas.length
              ? ` (para ver más: saltear=${saltear + pagina.length})`
              : '') +
            '. Cada creación es un renglón con su image_id y lo que muestra, y JUSTO DEBAJO su imagen. Elegí por el image_id del renglón que está ARRIBA de la imagen, y confirmá con el texto de "muestra".',
        },
      ];
      pagina.forEach((c, i) => {
        const muestra = descripciones.get(c.id)?.queMuestra;
        bloques.push({
          type: 'text',
          text: `Creación #${saltear + i + 1} · image_id=${c.id}${c.esVideo ? ' · VIDEO (reel)' : ' · foto'}${
            usadas.has(c.id) ? ' · ya está en un post' : ''
          }${muestra ? ` · muestra: «${muestra}»` : ''} · su imagen:`,
        });
        if (c.thumbUrl) bloques.push({ type: 'image', source: { type: 'url', url: c.thumbUrl } });
      });
      return { content: bloques };
    }

    case 'sugerir_horarios': {
      const cantidad = Math.min(MAX_POSTS_POR_LLAMADA, Math.max(1, Number(input.cantidad) || 1));
      const desde = leerFecha(input.desde);
      const hastaDia = leerFecha(input.hasta);
      const hasta = hastaDia
        ? new Date(hastaDia.getFullYear(), hastaDia.getMonth(), hastaDia.getDate(), 23, 59)
        : null;
      const { agenda, error } = await fetchAgendaDeLaEmpresa(pantalla.companyId);
      if (error) return fallo(error);
      const tomados: PostEnAgenda[] = [];
      const lineas: string[] = [];
      for (let i = 0; i < cantidad; i++) {
        const s = sugerirMomento({ agenda: [...agenda, ...tomados], ahora, desde, hasta });
        if (hasta && s.cuando > hasta) break;
        tomados.push({ id: `sugerido-${i}`, cuando: s.cuando });
        lineas.push(`• ${fechaParaElModelo(s.cuando)} — ${s.porque}`);
      }
      if (lineas.length === 0)
        return ok('No quedan horarios libres en ese rango. Probá con un rango más largo.');
      return ok(
        `${lineas.length} horario${lineas.length === 1 ? '' : 's'}` +
          (lineas.length < cantidad ? ` (pediste ${cantidad}; en ese rango no entran más)` : '') +
          `:\n${lineas.join('\n')}`,
      );
    }

    case 'crear_posts': {
      const pedidos = Array.isArray(input.posts) ? (input.posts as Array<Record<string, unknown>>) : [];
      if (pedidos.length === 0) return fallo('Mandá al menos un post en "posts".');
      if (pedidos.length > MAX_POSTS_POR_LLAMADA) {
        return fallo(`Hasta ${MAX_POSTS_POR_LLAMADA} posts por llamada; partilo en varias.`);
      }
      const creados: string[] = [];
      let primero: Date | null = null;
      const lineas: string[] = [];
      for (const [i, p] of pedidos.entries()) {
        const n = `#${i + 1}`;
        const cuando = leerFechaYHora(p.cuando);
        if (!cuando) {
          lineas.push(`${n}: no se creó — "cuando" tiene que ser "YYYY-MM-DD HH:mm"`);
          continue;
        }
        if (cuando.getTime() < ahora.getTime()) {
          lineas.push(`${n}: no se creó — ${fechaParaElModelo(cuando)} ya pasó`);
          continue;
        }
        const texto = typeof p.texto === 'string' ? p.texto.slice(0, MAX_TEXTO) : '';
        const notas = {
          ...(typeof p.planificacion === 'string' && p.planificacion.trim()
            ? { planificacion: p.planificacion }
            : {}),
          ...(typeof p.referencias === 'string' && p.referencias.trim()
            ? { referencias: p.referencias }
            : {}),
        };
        const piezas = listaDeIds(p.image_ids);
        const r = await crearPost(pantalla.lanzamientoId, pantalla.companyId, cuando);
        if (r.error || !r.id) {
          lineas.push(`${n}: no se creó — ${r.error ?? 'error desconocido'}`);
          continue;
        }
        creados.push(r.id);
        if (!primero || cuando < primero) primero = cuando;
        const avisos: string[] = [];
        if (texto || notas.planificacion || notas.referencias) {
          const g = await guardarPost(r.id, { ...(texto ? { texto } : {}), ...notas });
          if (g.error) avisos.push(`el texto no se guardó (${g.error})`);
        }
        const c = await colgarPiezas(pantalla.companyId, r.id, piezas, 0);
        if (c.problema) avisos.push(c.problema);
        lineas.push(
          `${n}: post_id=${r.id} · ${fechaParaElModelo(cuando)} · ${c.colgadas} pieza${c.colgadas === 1 ? '' : 's'}` +
            (c.cuales ? ` [${c.cuales}]` : '') +
            (avisos.length ? ` · ojo: ${avisos.join('; ')}` : ''),
        );
      }
      if (creados.length > 0) {
        pantalla.marcarDelAgente(creados);
        await pantalla.recargar();
        if (primero) pantalla.mostrarMes(primero);
      }
      return (creados.length > 0 ? ok : fallo)(
        `Se crearon ${creados.length} de ${pedidos.length} posts. Ya se ven en el calendario, marcados como armados por vos.\n${lineas.join('\n')}`,
      );
    }

    case 'modificar_post': {
      const id = typeof input.post_id === 'string' ? input.post_id : '';
      const { posts, error } = await fetchPosts(pantalla.lanzamientoId, pantalla.companyId);
      if (error) return fallo(error);
      const post = posts.find((p) => p.id === id);
      if (!post) return fallo(`No hay un post con post_id=${id} en este calendario. Mirá ver_calendario.`);
      if (post.estado === 'publicado') return fallo('Ese post ya se publicó: no se modifica.');

      const hechos: string[] = [];
      const problemas: string[] = [];
      const campos: { texto?: string; cuando?: Date; planificacion?: string; referencias?: string } = {};
      if (input.cuando !== undefined) {
        const cuando = leerFechaYHora(input.cuando);
        if (!cuando) problemas.push('"cuando" tiene que ser "YYYY-MM-DD HH:mm"');
        else if (cuando.getTime() < ahora.getTime()) problemas.push(`${fechaParaElModelo(cuando)} ya pasó`);
        else campos.cuando = cuando;
      }
      if (typeof input.texto === 'string') campos.texto = input.texto.slice(0, MAX_TEXTO);
      if (typeof input.planificacion === 'string') campos.planificacion = input.planificacion;
      if (typeof input.referencias === 'string') campos.referencias = input.referencias;
      if (Object.keys(campos).length > 0) {
        const g = await guardarPost(post.id, campos);
        if (g.error) problemas.push(g.error);
        else {
          if (campos.cuando) hechos.push(`ahora sale el ${fechaParaElModelo(campos.cuando)}`);
          if (campos.texto !== undefined) hechos.push('texto actualizado');
          if (campos.planificacion !== undefined) hechos.push('planificación actualizada');
          if (campos.referencias !== undefined) hechos.push('referencias actualizadas');
        }
      }

      const sacar = listaDeIds(input.sacar_pieza_ids);
      const propias = new Set(post.medios.map((m) => m.id));
      let quedan = post.medios.length;
      for (const piezaId of sacar) {
        if (!propias.has(piezaId)) {
          problemas.push(`pieza_id=${piezaId} no es de este post`);
          continue;
        }
        const q = await quitarImagen(piezaId);
        if (q.error) problemas.push(q.error);
        else quedan -= 1;
      }
      if (quedan < post.medios.length) hechos.push(`${post.medios.length - quedan} pieza(s) sacadas`);

      const c = await colgarPiezas(pantalla.companyId, post.id, listaDeIds(input.sumar_image_ids), quedan);
      if (c.colgadas) hechos.push(`${c.colgadas} pieza(s) sumadas: ${c.cuales}`);
      if (c.problema) problemas.push(c.problema);

      await pantalla.recargar();
      if (hechos.length === 0)
        return fallo(problemas.length ? problemas.join('; ') : 'No mandaste nada para cambiar.');
      return ok(`Listo: ${hechos.join(', ')}.${problemas.length ? ` Ojo: ${problemas.join('; ')}.` : ''}`);
    }

    case 'borrar_posts': {
      if (input.confirmado !== true) {
        return fallo('Primero decile al usuario qué posts vas a borrar y esperá que diga que sí.');
      }
      const ids = listaDeIds(input.post_ids);
      const { posts, error } = await fetchPosts(pantalla.lanzamientoId, pantalla.companyId);
      if (error) return fallo(error);
      const propios = new Map(posts.map((p) => [p.id, p]));
      let borrados = 0;
      const problemas: string[] = [];
      for (const id of ids) {
        const p = propios.get(id);
        if (!p) problemas.push(`post_id=${id} no está en este calendario`);
        else if (p.estado === 'publicado') problemas.push(`post_id=${id} ya se publicó`);
        else {
          const b = await borrarPost(id);
          if (b.error) problemas.push(b.error);
          else borrados += 1;
        }
      }
      if (borrados) await pantalla.recargar();
      return (borrados ? ok : fallo)(
        `Se borraron ${borrados} de ${ids.length}.${problemas.length ? ` ${problemas.join('; ')}.` : ''}`,
      );
    }

    case 'abrir_post': {
      const id = typeof input.post_id === 'string' ? input.post_id : '';
      const { posts, error } = await fetchPosts(pantalla.lanzamientoId, pantalla.companyId);
      if (error) return fallo(error);
      const post = posts.find((p) => p.id === id);
      if (!post) return fallo(`No hay un post con post_id=${id} en este calendario.`);
      const como = pantalla.abrirPost(id, post.cuando);
      return ok(
        como === 'esperando'
          ? 'Tenía otro post abierto con cambios sin guardar: le pregunté si los guarda o los descarta, y este se abre cuando elija.'
          : 'Abierto en el editor de la pantalla.',
      );
    }

    default:
      return fallo(`El calendario no tiene la herramienta ${nombre}.`);
  }
}
